import { BaseComponent } from '../../lib/BaseComponent.js';
import { api } from '../../lib/api.js';
import { authStyles } from './authStyles.js';

/**
 * Reset Password Page - consumes ?token= from the email link.
 */
export class ResetPasswordPage extends BaseComponent {
  private isLoading = false;
  private done = false;
  private errorMessage = '';

  private get token(): string {
    return new URLSearchParams(window.location.search).get('token') || '';
  }

  protected render(): void {
    this.addStyles(this.getCommonStyles());
    this.addStyles(authStyles());

    const container = this.createElement('div', { className: 'auth-container' });
    const header = this.createElement('div', { className: 'auth-header' });
    const title = this.createElement('h1');
    title.appendChild(this.createElement('a', { href: '/' }, 'FireGuard'));
    header.appendChild(title);
    header.appendChild(this.createElement('p', { className: 'subtitle' }, 'Choose a new password'));
    container.appendChild(header);

    const card = this.createElement('div', { className: 'auth-card' });
    card.appendChild(this.createElement('h2', {}, 'New password'));

    if (!this.token) {
      card.appendChild(this.createElement('div', { className: 'error-message' }, 'This reset link is missing its token. Request a new one.'));
    } else if (this.done) {
      card.appendChild(this.createElement('div', { className: 'success-message' }, 'Your password has been updated. Sign in with your new password.'));
    } else {
      if (this.errorMessage) card.appendChild(this.createElement('div', { className: 'error-message' }, this.errorMessage));
      const form = this.createElement('form', { className: 'auth-form' });
      form.addEventListener('submit', (e) => this.handleSubmit(e));

      const g1 = this.createElement('div', { className: 'form-group' });
      g1.appendChild(this.createElement('label', { for: 'password' }, 'New password'));
      g1.appendChild(this.createElement('input', { type: 'password', id: 'password', name: 'password', required: 'true', minlength: '8', autocomplete: 'new-password' }));
      g1.appendChild(this.createElement('span', { className: 'hint' }, 'At least 8 characters with a mix of letters and numbers.'));
      form.appendChild(g1);

      const g2 = this.createElement('div', { className: 'form-group' });
      g2.appendChild(this.createElement('label', { for: 'confirm' }, 'Confirm new password'));
      g2.appendChild(this.createElement('input', { type: 'password', id: 'confirm', name: 'confirm', required: 'true', autocomplete: 'new-password' }));
      form.appendChild(g2);

      const btn = this.createElement('button', { type: 'submit', className: 'btn-primary' }, this.isLoading ? 'Updating…' : 'Update password');
      if (this.isLoading) btn.setAttribute('disabled', 'true');
      form.appendChild(btn);
      card.appendChild(form);
    }

    const footer = this.createElement('div', { className: 'auth-footer' });
    const p = this.createElement('p');
    p.appendChild(this.createElement('a', { href: '/login' }, 'Back to sign in'));
    p.appendChild(document.createTextNode(' · '));
    p.appendChild(this.createElement('a', { href: '/forgot-password' }, 'Request a new link'));
    footer.appendChild(p);
    card.appendChild(footer);

    container.appendChild(card);
    this.shadow.appendChild(container);
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const data = new FormData(e.target as HTMLFormElement);
    const password = (data.get('password') as string) || '';
    const confirm = (data.get('confirm') as string) || '';
    if (password !== confirm) {
      this.errorMessage = 'Passwords do not match.';
      this.rerender();
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    this.rerender();
    try {
      await api.resetPassword(this.token, password);
      this.done = true;
    } catch (error) {
      this.errorMessage = (error as Error).message || 'Could not reset password';
    } finally {
      this.isLoading = false;
      this.rerender();
    }
  }

  private rerender(): void {
    this.shadow.replaceChildren();
    this.render();
  }
}

customElements.define('reset-password-page', ResetPasswordPage);
